import { useCallback, useState, memo, FC, useRef } from 'react';
import { useNavigate } from 'react-router';
import styled from 'styled-components';
import { Pin } from 'lucide-react';

import { APP_ROUTES } from '@navigation/types';
import { useUnauthorizedModal } from '@hooks/useUnauthorizedModal';
import { useElementSize } from '@hooks/useElementSize';
import theme from 'theme';
import { useAgoraEngine } from '@providers/AgoraEngineProvider';
import { useAuthUser } from '@redux/selectors/auth';
import { useCurrentUser, useUserDataById } from '@redux/selectors/users';
import { useChatMessageById } from '@redux/selectors/chat';
import { useChangePinnedStatusForChatMessageMutation } from '@redux/queries/chat';
import { VerificationRole } from '@utils/typings/user';
import { ChatMessageType } from '@utils/typings/show';
import TouchableOpacity from '@components/buttons/TouchableOpacity';
import { DefaultText, PremierText } from '@components/text';
import verifiedProIcon from '@assets/icons/verified-pro.png';
import verifiedUserIcon from '@assets/icons/verified-user.png';
import { SKEW_ANGLE } from '@utils/constants';

const GAP = 8;
const MAX_LINES = 2;
const MAX_GIF_SIZE = 240;
const DEFAULT_USERNAME_COLOR = theme.colors.white;

const Container = styled.div<{ $pinned: boolean }>`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 4px 0;
  gap: ${GAP}px;

  ${({ $pinned }) =>
    $pinned &&
    `
    padding-inline: 16px;
  `}
`;

const GradientBorder = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  border: 2px solid transparent;
  border-image: linear-gradient(
      90deg,
      ${({ theme }) => theme.colors.red[500]} 20%,
      ${({ theme }) => theme.colors.purple[500]} 50%,
      ${({ theme }) => theme.colors.blue[500]} 80%
    )
    1;
  transform: skewX(-${SKEW_ANGLE}deg);
`;

const UsernameWrapper = styled.div`
  max-width: 40%;
`;

const UsernameContainer = styled(TouchableOpacity)`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;

  &:disabled {
    cursor: default;
  }
`;

const VerifiedIcon = styled.img`
  width: 16px;
  height: 16px;
  margin-right: 4px;
  border-radius: 50%;
  border: 1px solid
    ${({ theme }) => `${theme.colors.black}${theme.opacity[15]}`};
`;

const Username = styled(PremierText)<{
  $color: string;
  $shouldShowShadow: boolean;
}>`
  font-size: ${({ theme }) => theme.fonts.fontSize.xs};
  color: ${({ $color }) => $color};
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  z-index: 1;

  ${({ $shouldShowShadow, theme }) =>
    $shouldShowShadow &&
    `
    text-shadow: 0 0.5px 0.5px ${theme.colors.white}${theme.opacity[75]};
  `}
`;

const MessageContent = styled(TouchableOpacity)`
  display: flex;
  flex: 1;
  cursor: pointer;
  z-index: 1;
`;

const MessageText = styled(DefaultText)<{
  $pinned?: boolean;
  $flex?: boolean;
  $isExpanded?: boolean;
}>`
  color: ${({ theme, $pinned }) =>
    $pinned ? theme.colors.white : theme.colors.grey[200]};
  font-size: ${({ theme }) => theme.fonts.fontSize.xs};
  font-weight: ${({ theme, $pinned }) =>
    $pinned ? theme.fonts.fontWeight.bold : theme.fonts.fontWeight.regular};
  text-align: left;

  ${({ $flex }) =>
    $flex &&
    `
    flex: 1;
  `}

  ${({ $pinned, $isExpanded }) =>
    $pinned &&
    !$isExpanded &&
    `
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: ${MAX_LINES};
    -webkit-box-orient: vertical;
  `}
`;

const GifImage = styled.img.attrs<{ $size: number }>(({ $size }) => ({
  style: {
    width: `${$size}px`,
    height: `${$size}px`,
  },
}))<{ $size: number }>`
  object-fit: cover;
`;

const PinButton = styled(TouchableOpacity)`
  padding: 0;
  margin: 0;
`;

const PinIcon = styled(Pin)<{ $pinned: boolean }>`
  width: 16px;
  height: 16px;
  color: ${({ theme }) => theme.colors.white};
  transform: ${({ $pinned }) => ($pinned ? 'rotate(0deg)' : 'rotate(45deg)')};
`;

type Props = {
  messageId: string;
  width?: number;
  pinned?: boolean;
};

const ChatMessage: FC<Props> = ({ messageId, width, pinned = false }) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const shouldShowShadow = false;
  const usernameContainerRef = useRef<HTMLDivElement>(null);
  const { isAnonymous } = useAuthUser();
  const { isHost, isCoHost } = useAgoraEngine();
  const chatMessage = useChatMessageById(messageId);
  const profile = useUserDataById(chatMessage?.userId);
  const currentUser = useCurrentUser();
  const navigate = useNavigate();
  const [pinMessage, { isLoading }] =
    useChangePinnedStatusForChatMessageMutation();
  const { openUnauthorizedModal } = useUnauthorizedModal();

  const { width: usernameWidth } = useElementSize(usernameContainerRef, {
    widthOnly: true,
  });

  const calculateGifSize = useCallback(() => {
    if (!width || !usernameWidth) {
      return MAX_GIF_SIZE;
    }

    const availableWidth = width - usernameWidth - GAP;
    return Math.min(availableWidth, MAX_GIF_SIZE);
  }, [width, usernameWidth]);

  const gifSize = calculateGifSize();

  const onNamePress = useCallback(() => {
    if (isAnonymous) {
      return openUnauthorizedModal();
    }

    if (profile?.id && profile?.id !== currentUser?.id) {
      navigate(`${APP_ROUTES.PROFILE}/${profile.id}`);
    }
  }, [
    profile?.id,
    navigate,
    currentUser?.id,
    openUnauthorizedModal,
    isAnonymous,
  ]);

  const onPinPress = useCallback(() => {
    if (isHost || isCoHost) {
      pinMessage({ id: messageId, showId: chatMessage?.showId });
    }
  }, [isHost, isCoHost, pinMessage, messageId, chatMessage?.showId]);

  const toggleExpand = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  return (
    <Container $pinned={pinned}>
      {pinned && <GradientBorder />}

      <UsernameWrapper ref={usernameContainerRef}>
        <UsernameContainer
          onClick={onNamePress}
          disabled={profile?.id === currentUser?.id}
        >
          {profile?.verifiedAs && (
            <VerifiedIcon
              src={
                profile.verifiedAs === VerificationRole.Pro
                  ? verifiedProIcon
                  : verifiedUserIcon
              }
              alt='verified'
            />
          )}

          <Username
            $color={profile?.usernameColor || DEFAULT_USERNAME_COLOR}
            $shouldShowShadow={shouldShowShadow}
          >
            {profile?.name || ''}
          </Username>
        </UsernameContainer>
      </UsernameWrapper>

      {chatMessage?.type === ChatMessageType.Gif ? (
        <GifImage src={chatMessage.content} alt='gif' $size={gifSize} />
      ) : (
        <>
          {pinned ? (
            <MessageContent onClick={toggleExpand}>
              <MessageText
                $pinned={pinned}
                $isExpanded={isExpanded}
                $flex={false}
              >
                {chatMessage?.content || ''}
              </MessageText>
            </MessageContent>
          ) : (
            <MessageText $flex>{chatMessage?.content || ''}</MessageText>
          )}

          {(isHost || isCoHost) && (
            <PinButton onClick={onPinPress} disabled={isLoading}>
              <PinIcon $pinned={pinned} />
            </PinButton>
          )}
        </>
      )}
    </Container>
  );
};

export default memo(ChatMessage);
